// src/pages/sow-analysis/AnalysisDetailModal.jsx
//
// Full analysis detail: score cards (always visible), then tabbed Overview
// (rationale + a lightweight Risk/Compliance/Commercial coverage card per
// dimension -- genuinely overview-level, no rule-by-rule detail here),
// Issues & Recommendations (severity chart + a structured issue table --
// the rule code behind an issue is secondary, so it's shown as a small
// note rather than as the row's headline), and Rule Evaluations (the one
// place the coverage donut and the score-calculation numbers live, next to
// a per-category score chart and the rule table itself, defaulting to just
// the rules that didn't fully pass -- that's the only time the rule name
// earns a look; a toggle reveals the full list for anyone who wants it).
// Originally split out of Sowanalysisviewerpage.jsx, which used to render
// all of this stacked in one permanently-visible right-hand panel.

import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { AlertTriangle, Download, FileDown, FileJson, Info, ListChecks } from "lucide-react";
import { Alert, Button, DataTable, Modal, ScoreChip, Select, SkeletonCardGrid, SkeletonTable, StatusBadge, Tabs } from "../../shared/ui";
import { coverageBucket, getScoreLabel, safeParseRaw, severityLabel, summarizeDimensionCoverage } from "./sowAnalysisHelpers";
import { CategoryScoreChart, IssuesSeverityChart, RuleCoveragePieChart } from "./AnalysisCharts";
import { SEVERITY_ORDER, SEVERITY_RANK, COVERAGE_LABELS } from "../../utils/constants";

/** Toggles a chart-click/dropdown filter: selecting the active value again clears it. */
function toggleFilter(setter) {
  return (value) => setter((cur) => (cur === value ? null : value));
}

const TABS = [
  { key: "overview", label: "Overview", icon: Info },
  { key: "issues", label: "Issues & Recommendations", icon: AlertTriangle },
  { key: "rules", label: "Rule Evaluations", icon: ListChecks },
];

function RuleChipList({ items, tone }) {
  const toneClass = tone === "missing" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200";
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((r) => (
        <span key={r.rule_name || r.name} className={`text-xs px-2 py-0.5 rounded-full border ${toneClass}`}>
          {r.rule_name || r.name}
        </span>
      ))}
    </div>
  );
}
RuleChipList.propTypes = { items: PropTypes.array.isRequired, tone: PropTypes.oneOf(["missing", "partial"]).isRequired };

// Stands in for one dimension's rationale sentence (e.g. "Risk: 0 of 8
// rules covered, 3 partially covered, 5 missing (RULE_A, RULE_B, ...).") --
// a coverage bar for the at-a-glance read, and the rule codes tucked behind
// a disclosure instead of crammed into the sentence itself.
function DimensionCoverageCard({ label, applicable, covered, partial, missing }) {
  const [expanded, setExpanded] = useState(false);
  const total = applicable.length;

  if (total === 0) {
    return (
      <div className="rounded-xl border border-line p-4">
        <p className="text-sm font-semibold text-ink mb-1">{label}</p>
        <p className="text-xs text-muted">No applicable rules for this contract type.</p>
      </div>
    );
  }

  const needsAttention = missing.length + partial.length;

  return (
    <div className="rounded-xl border border-line p-4 flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-ink">{label}</p>
        <p className="text-xs text-muted whitespace-nowrap">
          {covered.length} of {total} covered
        </p>
      </div>

      <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
        {covered.length > 0 && <div className="bg-green-500" style={{ width: `${(covered.length / total) * 100}%` }} />}
        {partial.length > 0 && <div className="bg-amber-500" style={{ width: `${(partial.length / total) * 100}%` }} />}
        {missing.length > 0 && <div className="bg-red-500" style={{ width: `${(missing.length / total) * 100}%` }} />}
      </div>

      {needsAttention > 0 && (
        <div>
          <button type="button" className="text-xs font-medium text-brand-700 hover:underline" onClick={() => setExpanded((v) => !v)}>
            {expanded ? "Hide" : "Show"} {needsAttention} rule{needsAttention === 1 ? "" : "s"} needing attention
          </button>
          {expanded && (
            <div className="mt-2 flex flex-col gap-2">
              {missing.length > 0 && <RuleChipList items={missing} tone="missing" />}
              {partial.length > 0 && <RuleChipList items={partial} tone="partial" />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
DimensionCoverageCard.propTypes = {
  label: PropTypes.string.isRequired,
  applicable: PropTypes.array.isRequired,
  covered: PropTypes.array.isRequired,
  partial: PropTypes.array.isRequired,
  missing: PropTypes.array.isRequired,
};

// A one-line summary by default -- the rule codes themselves are only
// useful once someone actually wants to check which ones, so they're
// tucked behind a disclosure rather than dumped as a wall of chips.
function MissingMandatoryRulesAlert({ names }) {
  const [expanded, setExpanded] = useState(false);

  if (names.length === 0) return null;

  return (
    <Alert variant="danger" title="Missing mandatory rules">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span>
          {names.length} mandatory rule{names.length === 1 ? "" : "s"} {names.length === 1 ? "is" : "are"} missing from this SOW.
        </span>
        <button
          type="button"
          className="text-xs font-medium text-red-700 hover:underline shrink-0"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Hide rules" : "View rules"}
        </button>
      </div>
      {expanded && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {names.map((name) => (
            <span key={name} className="text-xs px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200">
              {name}
            </span>
          ))}
        </div>
      )}
    </Alert>
  );
}
MissingMandatoryRulesAlert.propTypes = { names: PropTypes.array.isRequired };

function OverviewTab({ raw, summary, missingMandatory, ruleEvals }) {
  const riskCoverage = useMemo(() => summarizeDimensionCoverage(ruleEvals, "risk"), [ruleEvals]);
  const complianceCoverage = useMemo(() => summarizeDimensionCoverage(ruleEvals, "compliance"), [ruleEvals]);
  const commercialCoverage = useMemo(() => summarizeDimensionCoverage(ruleEvals, "commercial"), [ruleEvals]);

  return (
    <div className="flex flex-col gap-5">
      <div className="text-sm">
        <p className="font-semibold text-ink mb-0.5">Overall rationale</p>
        <p className="text-muted">{raw.overall_rationale || "—"}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <DimensionCoverageCard label="Risk" {...riskCoverage} />
        <DimensionCoverageCard label="Compliance" {...complianceCoverage} />
        <DimensionCoverageCard label="Commercial" {...commercialCoverage} />
      </div>

      <Alert variant="info">{summary || "No summary available."}</Alert>

      <MissingMandatoryRulesAlert names={missingMandatory} />
    </div>
  );
}

function IssuesTab({ issues, recommendations }) {
  const [severityFilter, setSeverityFilter] = useState(null);
  const onSelectSeverity = toggleFilter(setSeverityFilter);

  const severityOptions = useMemo(() => {
    const present = new Set(issues.map((i) => severityLabel(i.severity)));
    const opts = SEVERITY_ORDER.filter((s) => present.has(s)).map((s) => ({ value: s, label: s }));
    return [{ value: "", label: "All severities" }, ...opts];
  }, [issues]);

  const sortedIssues = useMemo(
    () =>
      [...issues].sort((a, b) => {
        const ra = SEVERITY_RANK[String(a.severity || "").toLowerCase()] ?? 99;
        const rb = SEVERITY_RANK[String(b.severity || "").toLowerCase()] ?? 99;
        return ra - rb;
      }),
    [issues]
  );

  const visibleIssues = useMemo(
    () => (severityFilter ? sortedIssues.filter((iss) => severityLabel(iss.severity) === severityFilter) : sortedIssues),
    [sortedIssues, severityFilter]
  );

  const columns = useMemo(
    () => [
      {
        id: "severity",
        header: "Severity",
        width: "110px",
        cell: (info) => <StatusBadge map="priority" status={info.row.original.severity} size="sm" />,
      },
      { id: "dimension", header: "Category", accessorFn: (r) => r.dimension || "—", width: "150px" },
      {
        id: "description",
        header: "Issue",
        cell: (info) => {
          const iss = info.row.original;
          return (
            <div className="py-0.5">
              <p className="text-ink">{iss.description || "—"}</p>
              {iss.rule_name && <p className="text-xs text-muted mt-1">Rule: {iss.rule_name}</p>}
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <p className="text-sm font-semibold text-ink">Issues by severity</p>
          <div className="w-40">
            <Select
              aria-label="Filter issues by severity"
              options={severityOptions}
              value={severityFilter || ""}
              onChange={(e) => setSeverityFilter(e.target.value || null)}
            />
          </div>
        </div>
        <div className="rounded-xl border border-line p-3">
          <IssuesSeverityChart issues={issues} selected={severityFilter} onSelect={onSelectSeverity} />
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-ink mb-2">
          Key Issues ({visibleIssues.length}
          {severityFilter ? ` of ${issues.length}` : ""})
        </p>
        {sortedIssues.length === 0 ? (
          <p className="text-sm text-muted">No issues returned.</p>
        ) : visibleIssues.length === 0 ? (
          <p className="text-sm text-muted">No issues match the selected severity.</p>
        ) : (
          <DataTable
            table={{ columns, rows: visibleIssues }}
            entriesPerPage={{ defaultValue: 10, entries: [10, 20, 50] }}
            emptyMessage="No issues."
          />
        )}
      </div>

      <div>
        <p className="text-sm font-semibold text-ink mb-2">Recommendations</p>
        {recommendations.length === 0 ? (
          <p className="text-sm text-muted">No recommendations returned.</p>
        ) : (
          <ul className="flex flex-col gap-2 text-sm text-ink">
            {recommendations.map((r, idx) => (
              <li key={idx} className="flex items-start gap-2 rounded-lg bg-gray-50 border border-line px-3 py-2">
                <span className="text-brand-600 font-semibold shrink-0">{idx + 1}.</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function RuleEvaluationsTab({ ruleEvals, raw }) {
  const [showAll, setShowAll] = useState(false);
  const [coverageFilter, setCoverageFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const onSelectCategory = toggleFilter(setCategoryFilter);

  // Selecting a coverage bucket (via dropdown or chart click) also forces
  // "show all" -- otherwise filtering to "Covered" would collide with the
  // default "needs attention" view and silently return nothing.
  const onSelectCoverage = (value) => {
    setCoverageFilter((cur) => {
      const next = cur === value ? null : value;
      if (next) setShowAll(true);
      return next;
    });
  };

  const coverageCounts = useMemo(() => {
    const counts = { covered: 0, partial: 0, missing: 0 };
    for (const r of ruleEvals) {
      const bucket = coverageBucket(r.status);
      if (bucket) counts[bucket] += 1;
    }
    return counts;
  }, [ruleEvals]);

  const coverageOptions = useMemo(() => {
    const opts = ["covered", "partial", "missing"]
      .filter((bucket) => coverageCounts[bucket] > 0)
      .map((bucket) => ({ value: COVERAGE_LABELS[bucket], label: COVERAGE_LABELS[bucket] }));
    return [{ value: "", label: "All coverage" }, ...opts];
  }, [coverageCounts]);

  const categoryOptions = useMemo(() => {
    const present = new Set(ruleEvals.map((r) => r.engine_category || "Uncategorized"));
    const opts = Array.from(present)
      .sort()
      .map((c) => ({ value: c, label: c }));
    return [{ value: "", label: "All categories" }, ...opts];
  }, [ruleEvals]);

  const totalWeight = raw.total_weight_used || raw.total_weight || 0;
  const weightedSum = raw.weighted_sum || 0;

  // The rule's exact name only earns a look once it hasn't fully passed --
  // default to that set; a full, weight-free list of every rule (covered
  // included) is one click away for anyone who wants it.
  const needsAttention = useMemo(() => ruleEvals.filter((r) => coverageBucket(r.status) !== "covered"), [ruleEvals]);

  const columns = useMemo(
    () => [
      {
        id: "rule_name",
        header: "Rule",
        cell: (info) => {
          const r = info.row.original;
          return <span className="block max-w-[220px] whitespace-normal break-words">{r.rule_name || r.name || "—"}</span>;
        },
      },
      {
        id: "status",
        header: "Status",
        width: "130px",
        cell: (info) => <StatusBadge map="coverage" status={info.row.original.status} />,
      },
      {
        id: "severity",
        header: "Severity",
        width: "100px",
        cell: (info) => <StatusBadge map="priority" status={info.row.original.engine_severity || info.row.original.severity} />,
      },
      { id: "category", header: "Category", accessorFn: (r) => r.engine_category || "—" },
      { id: "dimension", header: "Dimension", accessorFn: (r) => r.engine_dimension || "—" },
      {
        id: "mandatory",
        header: "Mandatory",
        align: "center",
        width: "100px",
        accessorFn: (r) => (r.engine_mandatory ? "Yes" : "No"),
      },
      { id: "rule_score", header: "Score", align: "right", width: "80px", accessorFn: (r) => r.rule_score ?? "—" },
      {
        id: "comments",
        header: "Comments",
        cell: (info) => <span className="block max-w-[320px] whitespace-normal break-words">{info.row.original.comments || "—"}</span>,
      },
    ],
    []
  );

  if (ruleEvals.length === 0) {
    return <p className="text-sm text-muted">No rule-by-rule details available.</p>;
  }

  const baseRows = showAll ? ruleEvals : needsAttention;
  const hasActiveFilter = Boolean(coverageFilter || categoryFilter);
  const visibleRows = baseRows.filter((r) => {
    if (coverageFilter && COVERAGE_LABELS[coverageBucket(r.status)] !== coverageFilter) return false;
    if (categoryFilter && (r.engine_category || "Uncategorized") !== categoryFilter) return false;
    return true;
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-line p-4 flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-sm font-semibold text-ink">Rule coverage &amp; score calculation</p>
            <div className="w-36">
              <Select
                aria-label="Filter rules by coverage"
                options={coverageOptions}
                value={coverageFilter || ""}
                onChange={(e) => onSelectCoverage(e.target.value || null)}
              />
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <RuleCoveragePieChart
              covered={coverageCounts.covered}
              partial={coverageCounts.partial}
              missing={coverageCounts.missing}
              selected={coverageFilter}
              onSelect={onSelectCoverage}
            />
            <div className="flex flex-col gap-1.5 text-xs">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded bg-green-500 shrink-0" /> Covered: {coverageCounts.covered}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded bg-amber-500 shrink-0" /> Partial: {coverageCounts.partial}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded bg-red-500 shrink-0" /> Missing: {coverageCounts.missing}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs border-t border-line pt-3">
            <div>
              <p className="text-muted">Formula</p>
              <p className="font-medium text-ink">(Weighted Sum ÷ Total Weight) × 100</p>
            </div>
            <div>
              <p className="text-muted">Total Rules</p>
              <p className="font-medium text-ink">{ruleEvals.length}</p>
            </div>
            <div>
              <p className="text-muted">Weighted Sum</p>
              <p className="font-medium text-ink">{weightedSum.toFixed(4)}</p>
            </div>
            <div>
              <p className="text-muted">Total Weight</p>
              <p className="font-medium text-ink">{totalWeight.toFixed(4)}</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-line p-4">
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <p className="text-sm font-semibold text-ink">Avg. score by category</p>
            <div className="w-40">
              <Select
                aria-label="Filter rules by category"
                options={categoryOptions}
                value={categoryFilter || ""}
                onChange={(e) => setCategoryFilter(e.target.value || null)}
              />
            </div>
          </div>
          <CategoryScoreChart ruleEvals={ruleEvals} selected={categoryFilter} onSelect={onSelectCategory} />
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-semibold text-ink">
          {hasActiveFilter
            ? `Filtered rules (${visibleRows.length} of ${baseRows.length})`
            : showAll
              ? `All rules (${ruleEvals.length})`
              : `Rules needing attention (${needsAttention.length})`}
        </p>
        {needsAttention.length < ruleEvals.length && (
          <Button size="sm" variant="secondary" onClick={() => setShowAll((v) => !v)}>
            {showAll ? "Show only rules needing attention" : `Show all ${ruleEvals.length} rules`}
          </Button>
        )}
      </div>

      {visibleRows.length === 0 ? (
        hasActiveFilter ? (
          <p className="text-sm text-muted">No rules match the selected filters.</p>
        ) : (
          <Alert variant="success">Every rule was fully covered.</Alert>
        )
      ) : (
        <DataTable
          table={{ columns, rows: visibleRows }}
          entriesPerPage={{ defaultValue: 5, entries: [5, 10, 20, 50] }}
          emptyMessage="No rule evaluations."
        />
      )}
    </div>
  );
}

export default function AnalysisDetailModal({
  open,
  onClose,
  detail,
  loading,
  onDownloadExcel,
  onDownloadSowFile,
  onDownloadJson,
  downloadingExcel = false,
  downloadingSowFile = false,
}) {
  const raw = useMemo(() => safeParseRaw(detail?.raw_json), [detail]);
  const [activeTab, setActiveTab] = useState("overview");

  const [lastDetailId, setLastDetailId] = useState(detail?.id);
  if (detail?.id !== lastDetailId) {
    setLastDetailId(detail?.id);
    setActiveTab("overview");
  }

  if (!open) return null;

  if (loading && !detail) {
    return (
      <Modal open={open} title="Analysis Detail" onClose={onClose} size="lg">
        <div className="flex flex-col gap-5">
          <SkeletonCardGrid cards={4} />
          <SkeletonTable rows={5} columns={4} />
        </div>
      </Modal>
    );
  }

  if (!detail) {
    return (
      <Modal open={open} title="Analysis Detail" onClose={onClose} size="lg">
        <p className="text-sm text-muted">Record not found.</p>
      </Modal>
    );
  }

  const finalOverall = raw.final_overall_score_used ?? raw.computed_overall_score ?? detail.score_overall ?? 0;
  const riskScore = raw.risk_score ?? detail.score_risk;
  const complianceScore = raw.compliance_score ?? detail.score_compliance;
  const commercialScore = raw.commercial_score ?? detail.score_commercial;

  const issues = raw.issues || [];
  const recommendations = raw.recommendations || [];
  const ruleEvals = raw.rule_evaluations_enriched || raw.rule_evaluations || [];
  const missingMandatory = raw.missing_mandatory_rules || [];
  // Only analyses created after original files started being stored have one.
  const hasFile = Boolean(detail.has_source_document);

  return (
    <Modal
      open={open}
      title={`Analysis #${detail.id} — ${detail.opportunity_name || ""}`}
      onClose={onClose}
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <dl className="text-sm text-ink grid grid-cols-2 gap-x-6 gap-y-1">
            <div className="flex gap-1.5">
              <dt className="text-muted">Account:</dt>
              <dd>{detail.account_name || "—"}</dd>
            </div>
            <div className="flex gap-1.5">
              <dt className="text-muted">Contract Type:</dt>
              <dd>{detail.contract_type || "—"}</dd>
            </div>
            <div className="flex gap-1.5">
              <dt className="text-muted">Source:</dt>
              <dd>
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    detail.source === "SFDC" ? "bg-blue-100 text-blue-700" : "bg-violet-100 text-violet-700"
                  }`}
                >
                  {detail.source || "—"}
                </span>
              </dd>
            </div>
            <div className="flex gap-1.5">
              <dt className="text-muted">Stage:</dt>
              <dd>{detail.stage || "—"}</dd>
            </div>
            {detail.sf_opportunity_id && (
              <div className="flex gap-1.5 col-span-2">
                <dt className="text-muted">SF ID:</dt>
                <dd>{detail.sf_opportunity_id}</dd>
              </div>
            )}
          </dl>
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onDownloadExcel(detail.id)}
              loading={downloadingExcel}
              title="Download the Excel analysis report"
            >
              {!downloadingExcel && <FileDown size={14} />} Excel
            </Button>
            <Button size="sm" variant="secondary" onClick={() => onDownloadJson(detail.id)}>
              <FileJson size={14} /> JSON
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onDownloadSowFile(detail.id)}
              disabled={!hasFile}
              loading={downloadingSowFile}
              title={hasFile ? "Download the original SOW" : "Original file isn't stored for this analysis"}
            >
              {!downloadingSowFile && <Download size={14} />} SOW
            </Button>
          </div>
        </div>

        <div className="flex gap-3 flex-wrap">
          <ScoreChip size="lg" label={`Overall — ${getScoreLabel(finalOverall)}`} score={finalOverall} />
          <ScoreChip size="lg" label={`Risk — ${getScoreLabel(riskScore)}`} score={riskScore} />
          <ScoreChip size="lg" label={`Compliance — ${getScoreLabel(complianceScore)}`} score={complianceScore} />
          <ScoreChip size="lg" label={`Commercial — ${getScoreLabel(commercialScore)}`} score={commercialScore} />
        </div>

        <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

        <div className="min-h-[360px]">
          {activeTab === "overview" && (
            <OverviewTab raw={raw} summary={raw.summary || detail.summary} missingMandatory={missingMandatory} ruleEvals={ruleEvals} />
          )}
          {activeTab === "issues" && <IssuesTab key={detail.id} issues={issues} recommendations={recommendations} />}
          {activeTab === "rules" && <RuleEvaluationsTab key={detail.id} ruleEvals={ruleEvals} raw={raw} />}
        </div>
      </div>
    </Modal>
  );
}

AnalysisDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  detail: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  onDownloadExcel: PropTypes.func.isRequired,
  onDownloadSowFile: PropTypes.func.isRequired,
  onDownloadJson: PropTypes.func.isRequired,
  downloadingExcel: PropTypes.bool,
  downloadingSowFile: PropTypes.bool,
};
